import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { getCurrentPermissions } from '@/api/authApi';
import { clearAuthTokens } from '@/utils/authStorage';

interface UserInfo {
  user_id: string;
  tenant_id: string;
  username: string;
  display_name: string;
  locale: string;
}

interface Permission {
  module: string;
  action: string;
  resource?: string;
}

interface SessionResponse {
  authenticated: boolean;
  user: {
    id: string;
    username: string;
    displayName: string;
    companyId: string;
    permissions: string[];
    roles: string[];
  };
}

interface AuthContextValue {
  user: UserInfo | null;
  permissions: Permission[];
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (module: string, action: string) => boolean;
  refreshSession: () => Promise<boolean>;
  refreshPermissions: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = useMemo(() => !!user, [user]);

  const refreshPermissions = useCallback(async () => {
    try {
      const response = await getCurrentPermissions();
      setPermissions(response.data ?? []);
    } catch {
      setPermissions([]);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    clearAuthTokens();
    setLoading(true);
    try {
      const response = await fetch('/auth/session', {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        setUser(null);
        setPermissions([]);
        return false;
      }

      const session = await response.json() as SessionResponse;
      if (!session.authenticated || !session.user?.id) {
        setUser(null);
        setPermissions([]);
        return false;
      }

      setUser({
        user_id: session.user.id,
        tenant_id: session.user.companyId,
        username: session.user.username,
        display_name: session.user.displayName || session.user.username,
        locale: 'zh-TW',
      });
      await refreshPermissions();
      return true;
    } catch {
      setUser(null);
      setPermissions([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshPermissions]);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const hasPermission = useCallback(
    (module: string, action: string): boolean => {
      if (!isAuthenticated) return false;
      return permissions.some((permission) =>
        permission.module === module && permission.action === action,
      );
    },
    [isAuthenticated, permissions],
  );

  const logout = useCallback(() => {
    setLoading(true);
    void fetch('/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-WEMS-CSRF': '1' },
    }).finally(() => {
      clearAuthTokens();
      setUser(null);
      setPermissions([]);
      setLoading(false);
      window.location.replace('https://login.sun-bd.com/#/services');
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      isAuthenticated,
      loading,
      hasPermission,
      refreshSession,
      refreshPermissions,
      logout,
    }),
    [
      user,
      permissions,
      isAuthenticated,
      loading,
      hasPermission,
      refreshSession,
      refreshPermissions,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
